import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../services/supabase';
import {
  PlannerCard,
  PlannerError,
  PlannerHeading,
  PlannerInput,
  PlannerKicker,
  PlannerPage,
  PlannerSubtle,
  plannerColors,
} from './plannerTheme';
import { parseSongTemplateDurationInput } from './songDuration';

const FormCard = styled(PlannerCard)`
  display: grid;
  gap: 0.85rem;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.7rem;
  flex-wrap: wrap;
`;

const BreadcrumbLink = styled(Link)`
  color: ${plannerColors.accent};
  font-size: 0.8rem;
  text-decoration: none;
  font-weight: 700;

  &:hover {
    text-decoration: underline;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  font-size: 0.78rem;
  color: ${plannerColors.text};
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 700;
`;

const TextArea = styled.textarea`
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  border-radius: 10px;
  padding: 0.62rem 0.75rem;
  min-height: 92px;
  resize: vertical;
`;

const SequenceTokenRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
`;

const SequenceTokenButton = styled.button`
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  border-radius: 999px;
  padding: 0.3rem 0.56rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.55rem;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  border: 1px solid rgba(241, 226, 198, 0.5);
  background: linear-gradient(140deg, #f1e2c6 0%, #dcc19a 100%);
  color: #171717;
  border-radius: 10px;
  padding: 0.6rem 0.95rem;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const GhostButton = styled.button`
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  border-radius: 10px;
  padding: 0.56rem 0.9rem;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
`;

const initialForm = {
  title: '',
  artist: '',
  keySignature: '',
  lengthMinutes: '5',
  bpm: '',
  sortOrder: '0',
  songSequence: '',
  notes: '',
  lyrics: '',
  lyricsUrl: '',
  chordChartUrl: '',
  videoUrl: '',
  themes: '',
};

const sequenceTokens = [
  'Intro',
  'Verse 1',
  'Verse 2',
  'Pre-Chorus',
  'Chorus',
  'Bridge',
  'Vamp',
  'Inst',
  'Tag',
  'Outro',
];

function parseThemes(value: string): string[] | null {
  const normalized = value.trim();
  if (!normalized) return null;
  const parts = normalized
    .split(',')
    .map((theme) => theme.trim())
    .filter((theme) => theme.length > 0);
  return parts.length ? parts : null;
}

export default function ChurchPlannerSongCreatePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [errorText, setErrorText] = useState('');

  const createSong = async () => {
    const title = form.title.trim();
    const sequence = Number.parseInt(form.sortOrder.trim(), 10);
    const bpm = form.bpm.trim() ? Number.parseInt(form.bpm.trim(), 10) : null;
    const parsedDuration = parseSongTemplateDurationInput(form.lengthMinutes);

    if (!title) {
      setErrorText('Song title is required.');
      return;
    }

    if (!parsedDuration.value) {
      setErrorText(parsedDuration.error || 'Duration must be minutes (5) or m:ss (4:53).');
      return;
    }

    if (!Number.isFinite(sequence) || sequence < 0) {
      setErrorText('Sequence must be 0 or greater.');
      return;
    }

    if (bpm != null && (!Number.isFinite(bpm) || bpm <= 0)) {
      setErrorText('BPM must be greater than 0.');
      return;
    }

    setSaving(true);
    setErrorText('');

    const { data, error } = await supabase
      .from('service_song_templates')
      .insert({
        title,
        artist: form.artist.trim() || null,
        key_signature: form.keySignature.trim() || null,
        length_minutes: parsedDuration.value.lengthMinutes,
        length_seconds: parsedDuration.value.lengthSeconds,
        bpm,
        sequence,
        song_sequence: form.songSequence.trim() || null,
        notes: form.notes.trim() || null,
        lyrics: form.lyrics.trim() || null,
        lyrics_url: form.lyricsUrl.trim() || null,
        chord_chart_url: form.chordChartUrl.trim() || null,
        video_url: form.videoUrl.trim() || null,
        themes: parseThemes(form.themes),
        created_by: user?.id || null,
      })
      .select('id')
      .single();

    setSaving(false);

    if (error) {
      setErrorText(error.message || 'Unable to save song template.');
      return;
    }

    navigate(`/church-planner/songs/${data.id}`);
  };

  return (
    <PlannerPage>
      <PlannerCard>
        <HeaderRow>
          <PlannerKicker style={{ margin: 0 }}>Planner Music</PlannerKicker>
          <BreadcrumbLink to="/church-planner/songs">Back to Songs</BreadcrumbLink>
        </HeaderRow>
        <PlannerHeading style={{ marginTop: '0.35rem' }}>New Song Template</PlannerHeading>
        <PlannerSubtle>Create a reusable song template for upcoming services.</PlannerSubtle>
      </PlannerCard>

      <FormCard>
        <FormRow>
          <Label>
            Title
            <PlannerInput value={form.title} onChange={(event) => setForm((curr) => ({ ...curr, title: event.target.value }))} />
          </Label>
          <Label>
            Artist
            <PlannerInput value={form.artist} onChange={(event) => setForm((curr) => ({ ...curr, artist: event.target.value }))} />
          </Label>
        </FormRow>

        <FormRow>
          <Label>
            Key
            <PlannerInput value={form.keySignature} onChange={(event) => setForm((curr) => ({ ...curr, keySignature: event.target.value }))} />
          </Label>
          <Label>
            Duration (m:ss or minutes)
            <PlannerInput value={form.lengthMinutes} onChange={(event) => setForm((curr) => ({ ...curr, lengthMinutes: event.target.value }))} placeholder="4:53 or 5" />
          </Label>
        </FormRow>

        <FormRow>
          <Label>
            BPM
            <PlannerInput value={form.bpm} onChange={(event) => setForm((curr) => ({ ...curr, bpm: event.target.value }))} />
          </Label>
          <Label>
            Sort Order
            <PlannerInput value={form.sortOrder} onChange={(event) => setForm((curr) => ({ ...curr, sortOrder: event.target.value }))} />
          </Label>
        </FormRow>

        <Label>
          Song Sequence
          <TextArea
            value={form.songSequence}
            onChange={(event) => setForm((curr) => ({ ...curr, songSequence: event.target.value }))}
            placeholder="Intro, Verse 1 2x, Pre-Chorus 1x, Chorus 1x, Verse 2, Vamp, Inst"
          />
        </Label>

        <SequenceTokenRow>
          {sequenceTokens.map((token) => (
            <SequenceTokenButton
              key={token}
              type="button"
              onClick={() => {
                setForm((curr) => {
                  const base = curr.songSequence.trim();
                  return {
                    ...curr,
                    songSequence: base ? `${base}, ${token}` : token,
                  };
                });
              }}
            >
              {token}
            </SequenceTokenButton>
          ))}
        </SequenceTokenRow>

        <FormRow>
          <Label>
            Themes (comma separated)
            <PlannerInput value={form.themes} onChange={(event) => setForm((curr) => ({ ...curr, themes: event.target.value }))} />
          </Label>
          <Label>
            Lyrics URL
            <PlannerInput value={form.lyricsUrl} onChange={(event) => setForm((curr) => ({ ...curr, lyricsUrl: event.target.value }))} />
          </Label>
        </FormRow>

        <Label>
          Chord Chart URL
          <PlannerInput value={form.chordChartUrl} onChange={(event) => setForm((curr) => ({ ...curr, chordChartUrl: event.target.value }))} />
        </Label>

        <Label>
          Video URL
          <PlannerInput value={form.videoUrl} onChange={(event) => setForm((curr) => ({ ...curr, videoUrl: event.target.value }))} />
        </Label>

        <Label>
          Notes
          <TextArea value={form.notes} onChange={(event) => setForm((curr) => ({ ...curr, notes: event.target.value }))} />
        </Label>

        <Label>
          Lyrics
          <TextArea value={form.lyrics} onChange={(event) => setForm((curr) => ({ ...curr, lyrics: event.target.value }))} />
        </Label>

        <ButtonRow>
          <PrimaryButton type="button" onClick={() => void createSong()} disabled={saving}>
            {saving ? 'Saving...' : 'Create Template'}
          </PrimaryButton>
          <GhostButton type="button" onClick={() => navigate('/church-planner/songs')}>
            Cancel
          </GhostButton>
        </ButtonRow>
      </FormCard>

      {errorText ? <PlannerError>{errorText}</PlannerError> : null}
    </PlannerPage>
  );
}
